import {useI18n} from 'i18n/I18nContext'
import React, {useEffect, useState} from 'react'
import {useEditor} from 'TemplateEditor/EditorProvider'
import {SectionsList} from 'TemplateEditor/Layout/Sidebar/SectionsList'
import {BlocksList} from 'TemplateEditor/Layout/Sidebar/BlocksList'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ViewDayIcon from '@mui/icons-material/ViewDay'
import WidgetsIcon from '@mui/icons-material/Widgets'
import {ExampleTemplates} from 'TemplateEditor/Layout/Sidebar/ExampleTemplates'
import SmartphoneIcon from '@mui/icons-material/Smartphone'
import LaptopMacIcon from '@mui/icons-material/LaptopMac'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import {AiTemplateSidebar} from 'TemplateEditor/Layout/Sidebar/AiTemplateSidebar'
import {TemplateGlobalSettings} from 'TemplateEditor/Layout/Sidebar/TemplateGlobalSettings'

export function Sidebar() {
  const {t} = useI18n()
  const {editMode, setEditMode, mobileMode, setMobileMode, setKeepSidebar} =
    useEditor()

  useEffect(() => {
    setKeepSidebar(true)
  }, [setKeepSidebar])

  type Tabs = 'sections' | 'elements'

  const getLastTab = (): Tabs => {
    return (localStorage.getItem('lastEditorTab') as Tabs) ?? 'sections'
  }

  const [tab, setTab] = useState<Tabs>(getLastTab())
  const [showAi, setShowAi] = useState(false)

  const handleTabChange = (tab: Tabs) => {
    setTab(tab)
    saveLastTab(tab)
  }

  const saveLastTab = (tab: Tabs) => {
    localStorage.setItem('lastEditorTab', tab)
  }

  const handleToggleEditMode = () => setEditMode(!editMode)
  const handleToggleMobileMode = () => setMobileMode(!mobileMode)
  return (
    <>
      <div className={'offcanvas__editor info-open'}>
        <div className="offcanvas__wrapper">
          <div
            className={
              showAi
                ? 'offcanvas__content'
                : 'offcanvas__content template-editor-sidebar-split'
            }
          >
            {showAi ? (
              <AiTemplateSidebar onBack={() => setShowAi(false)} />
            ) : (
              <>
                <div className="template-editor-sidebar-sticky">
                  <div className="accordion__tab">
                    <nav>
                      <div className={`bd-theme-toggle mb-20 d-block`}>
                        <label
                          className="bd-theme-toggle-main"
                          style={{width: '100%'}}
                          htmlFor="bd-theme-toggler"
                        >
                          <span
                            className={`bd-theme-toggle-dark ${editMode ? 'active' : ''}`}
                          >
                            <EditIcon style={{fontSize: 18}} />{' '}
                            {t('templateEditor.sidebarEdit')}
                          </span>
                          <input
                            type="checkbox"
                            id="bd-theme-toggler"
                            checked={editMode}
                            onChange={handleToggleEditMode}
                          />
                          <i className="bd-theme-toggle-slide"></i>
                          <span
                            className={`bd-theme-toggle-light ${!editMode ? 'active' : ''}`}
                          >
                            <VisibilityIcon style={{fontSize: 18}} />{' '}
                            {t('templateEditor.sidebarPreview')}
                          </span>
                        </label>
                      </div>

                      <div className={`bd-theme-toggle mb-20 d-block`}>
                        <label
                          className="bd-theme-toggle-main"
                          style={{width: '100%'}}
                          htmlFor="bd-theme-toggler2"
                        >
                          <span
                            className={`bd-theme-toggle-dark ${!mobileMode ? 'active' : ''}`}
                          >
                            <LaptopMacIcon style={{fontSize: 18}} />{' '}
                            {t('templateEditor.sidebarDesktop')}
                          </span>
                          <input
                            type="checkbox"
                            id="bd-theme-toggler2"
                            checked={!mobileMode}
                            onChange={handleToggleMobileMode}
                          />
                          <i className="bd-theme-toggle-slide"></i>
                          <span
                            className={`bd-theme-toggle-light ${mobileMode ? 'active' : ''}`}
                          >
                            <SmartphoneIcon style={{fontSize: 18}} />{' '}
                            {t('templateEditor.sidebarMobile')}
                          </span>
                        </label>
                      </div>

                      <button
                        type="button"
                        className="w-100 template-editor-ai-btn mb-20"
                        onClick={() => setShowAi(true)}
                      >
                        <AutoAwesomeIcon style={{fontSize: 16}} />
                        {t('templateEditor.sidebarAiGenerate')}
                      </button>

                      <div className="nav nav-tabs template-editor-nav-tabs mb-0">
                        <button
                          className={`nav-link ${tab === 'sections' ? 'active' : ''}`}
                          type="button"
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 4,
                          }}
                          onClick={() => handleTabChange('sections')}
                        >
                          <ViewDayIcon style={{fontSize: 16}} />
                          {t('templateEditor.sidebarSectionsTab')}
                        </button>
                        <button
                          className={`nav-link ${tab === 'elements' ? 'active' : ''}`}
                          data-bs-toggle="tab"
                          data-bs-target="#editor-tab-blocks"
                          type="button"
                          role="tab"
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 4,
                          }}
                          onClick={() => handleTabChange('elements')}
                        >
                          <WidgetsIcon style={{fontSize: 16}} />
                          {t('templateEditor.sidebarElementsTab')}
                        </button>
                      </div>
                    </nav>
                  </div>
                </div>

                <div className="template-editor-sidebar-scroll">
                  <div className="tab-content" id="nav-tabContent">
                    <div
                      className={`tab-pane ${tab === 'sections' ? 'show active' : ''}`}
                      id="editor-tab-sections"
                      role="tabpanel"
                      tabIndex={0}
                    >
                      <SectionsList />
                    </div>

                    <div
                      className={`tab-pane ${tab === 'elements' ? 'show active' : ''}`}
                      id="editor-tab-blocks"
                      role="tabpanel"
                      tabIndex={1}
                    >
                      <BlocksList />
                    </div>
                  </div>
                </div>

                <div className="template-editor-sidebar-designs">
                  <TemplateGlobalSettings />
                  <ExampleTemplates />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
